import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { cartService } from '@/features/cart/services/cartService';
import type { CartResponseWithRelatedData } from '@/features/cart/types';
import { CartStatus } from '@/features/cart/types';
import { env } from '@/app/config/environment';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

import { usePaymentTimer } from '@/features/cart/hooks/usePaymentTimer';
import CartStatusBanner from '@/features/cart/components/CartStatusBanner';
import PaymentSuccessModal from '@/features/cart/components/PaymentSuccessModal';
import PaymentFailureModal from '@/features/cart/components/PaymentFailureModal';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrencyForDisplay } from '@/lib/utils';
import { toast } from 'react-hot-toast';


// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PaymentIntent = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StripeError = any;

/**
 * Payment Form Component - Handles the actual payment submission
 */
const CheckoutForm: React.FC<{ 
  cartId: string;
  onSuccess: (paymentIntent: PaymentIntent) => void;
  onError: (error: StripeError) => void;
  timeRemaining?: number | null;
  formattedTime?: string;
  isExpired?: boolean;
  onCancel?: () => void;
  isCanceling?: boolean;
}> = ({ cartId, onSuccess, onError, timeRemaining = null, formattedTime = '', isExpired = false, onCancel, isCanceling = false }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    const returnUrl = `${window.location.origin}/carts/${cartId}?payment=success`;

    // It handles creating the PM and confirming the PI in one step.
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      // We set redirect: 'if_required' to handle the result
      // immediately if no 3DS is needed.
      redirect: 'if_required',
    });

    // This point will only be reached if there is an immediate error or
    // if the payment completed without a redirect (e.g., no 3DS).
    if (error) {
      const errorMsg = error.message || 'An unexpected error occurred.';
      setErrorMessage(errorMsg);
      console.error('Stripe confirmPayment error:', error);
      setLoading(false);
      onError(error);
      return;
    }

    // If we are here, the payment was successful without a redirect.
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      setLoading(false);
      onSuccess(paymentIntent);
    } else {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Payment Element - Shows card inputs + Apple Pay/Google Pay automatically */}
      <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700/50">
        <PaymentElement />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Timer Text - Minimal and Elegant */}
      {!isExpired && timeRemaining !== null && formattedTime && (
        <div className="text-center">
          <p className="text-gray-400 text-base">
            Pay within <span className="text-white font-semibold text-lg">{formattedTime}</span>
            {timeRemaining < 60 && (
              <span className="text-yellow-400 ml-2">• Less than a minute left</span>
            )}
          </p>
        </div>
      )}

      {/* Submit Button - Hide if expired */}
      {!isExpired && (
        <button
          type="submit"
          disabled={!stripe || loading}
          className={`w-full px-6 py-4 rounded-lg font-medium text-lg transition-all duration-200 ${
            stripe && !loading
              ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg shadow-green-500/20'
              : 'bg-gray-800 text-gray-400 cursor-not-allowed'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            'Pay Now'
          )}
        </button>
      )}

      {/* Cancel Checkout Button - Yellow, below Pay Now button */}
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isCanceling}
          className="w-full px-6 py-3 rounded-lg font-medium text-base transition-all duration-200 bg-yellow-600 hover:bg-yellow-700 text-white shadow-lg shadow-yellow-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCanceling ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Canceling...
            </span>
          ) : (
            'Cancel Checkout'
          )}
        </button>
      )}
    </form>
  );
};

/**
 * Main Payment Page Component
 */
const PaymentPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, isGuest } = useAuth();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartData, setCartData] = useState<CartResponseWithRelatedData | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [paymentIntentData, setPaymentIntentData] = useState<PaymentIntent | null>(null);
  const [paymentError, setPaymentError] = useState<StripeError | null>(null);
  const [isCancelingCheckout, setIsCancelingCheckout] = useState(false);

  // Payment timer
  // Ref to hold expired cancel handler for the callback
  const expiredCancelRef = useRef<(() => void) | null>(null);

  const handleTimerExpire = useCallback(() => {
    expiredCancelRef.current?.();
  }, []);

  const { timeRemaining, isExpired, formattedTime } = usePaymentTimer(cartData?.cart || null, {
    onExpire: handleTimerExpire,
  });

  // Handle timer expiration on payment page
  // On success: redirect to cart page
  // On failure: do nothing (payment may have succeeded, wait for success modal)
  const handleExpiredCancelCheckout = useCallback(async () => {
    if (!cartId) return;
    
    setIsCancelingCheckout(true);
    try {
      await cartService.cancelCheckout(cartId);
      // On success, redirect to cart page
      navigate(`/carts/${cartId}`);
    } catch (error) {
      // On failure, do nothing - payment may have succeeded
      // User will see the payment success modal if it did
      console.log('Cancel checkout failed - payment may have succeeded');
    } finally {
      setIsCancelingCheckout(false);
    }
  }, [cartId, navigate]);

  // Update the ref with the latest handler
  useEffect(() => {
    expiredCancelRef.current = handleExpiredCancelCheckout;
  }, [handleExpiredCancelCheckout]);

  // Wrapper function to handle manual cancel checkout with redirect and toast
  const handleCancelCheckout = async () => {
    if (!cartId) return;
    
    setIsCancelingCheckout(true);
    try {
      await cartService.cancelCheckout(cartId);
      // Refetch cart data
      const response = await cartService.getCart(cartId);
      setCartData(response);
      // Redirect to cart page on success
      navigate(`/carts/${cartId}`);
    } catch (error) {
      console.error('Error canceling checkout:', error);
      // Show error toast
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsCancelingCheckout(false);
    }
  };

  // We *always* use the main platform's key
  const platformPublishableKey = env.STRIPE_PUBLISHABLE_KEY;

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      if (!cartId) {
        setError('Cart ID is required');
        setLoading(false);
        return;
      }

      // Proactive check: Skip API call if not authenticated
      if (!authLoading && !isAuthenticated) {
        setLoading(false);
        setError('Please log in to proceed with payment');
        navigate('/login');
        return;
      }
      
      // Wait for auth check to complete
      if (authLoading) return;

      if (!platformPublishableKey) {
        setError('Stripe is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      try {
        // Always fetch cart data first for order summary
        const response: CartResponseWithRelatedData = await cartService.getCart(cartId);
        setCartData(response);

        // Get the connected account ID from the API response
        // This will be used as a fallback if checkout doesn't provide it
        if (response.stripe_account_id) {
          setStripeAccountId(response.stripe_account_id);
        }

        // Check if cart already has client_secret (from previous checkout)
        if (response.client_secret) {
          // Validate the client_secret format
          if (response.client_secret.startsWith('pi_') && response.client_secret.includes('_secret_')) {
            setClientSecret(response.client_secret);
            
            // Ensure we have stripe_account_id before proceeding
            if (!response.stripe_account_id) {
              console.error('stripe_account_id missing from cart response with existing client_secret');
              throw new Error('Payment configuration error: Stripe account ID is missing. Please contact support.');
            }
            
            setLoading(false);
            return;
          } else {
            console.warn('Invalid client_secret format in cart response');
          }
        }

        // Only call checkoutCart if cart is ACTIVE (not already in PENDING_PAYMENT or FAILED_PAYMENT)
        // If cart is PENDING_PAYMENT or FAILED_PAYMENT, it means checkout was already called
        // and we should use the existing client_secret (which should be in the response above)
        if (response.cart.status === CartStatus.Active) {
          // Cart is active, create payment intent via checkout
          const checkoutResponse = await cartService.checkoutCart(cartId);
          if (checkoutResponse.client_secret) {
            // Validate the client_secret format
            if (checkoutResponse.client_secret.startsWith('pi_') && checkoutResponse.client_secret.includes('_secret_')) {
              setClientSecret(checkoutResponse.client_secret);
              
              // Prioritize stripe_account_id from checkout response (most authoritative)
              // Fall back to response.stripe_account_id if checkout doesn't provide it
              const accountId = checkoutResponse.stripe_account_id || response.stripe_account_id;
              if (accountId) {
                setStripeAccountId(accountId);
              } else {
                // Both getCart and checkoutCart failed to provide stripe_account_id
                console.error('stripe_account_id missing from both getCart and checkoutCart responses');
                throw new Error('Payment configuration error: Stripe account ID is missing. Please contact support.');
              }
            } else {
              throw new Error('Invalid client_secret format returned from checkout');
            }
          } else {
            throw new Error('No client_secret returned from checkout');
          }
        } else if (response.cart.status === CartStatus.PendingPayment || response.cart.status === CartStatus.FailedPayment) {
          // Cart is in pending or failed payment state, allow proceeding with existing client_secret
          if (!response.client_secret) {
            throw new Error('Payment session expired. Please return to cart and try again.');
          }
        } else {
          throw new Error(`Cannot proceed to payment. Cart status: ${response.cart.status}`);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching payment intent:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment. Please try again.';
        setError(errorMessage);
        setLoading(false);
        
        // If 401, redirect to login
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosError = err as { response?: { status?: number }; status?: number };
          if (axiosError?.response?.status === 401 || axiosError?.status === 401) {
            navigate('/login');
            return;
          }
        }
      }
    };

    fetchPaymentIntent();
  }, [cartId, navigate, platformPublishableKey, isAuthenticated, authLoading]);

  // Pass stripeAccount to loadStripe
  const stripePromise = useMemo(() => {
    if (!platformPublishableKey || !stripeAccountId) {
      return null;
    }
    // Load Stripe with the platform's key AND the connected account's ID
    return loadStripe(platformPublishableKey, {
      stripeAccount: stripeAccountId,
    });
  }, [platformPublishableKey, stripeAccountId]);

  // Options for Stripe Elements
  const options = useMemo(() => {
    if (!clientSecret) return null;
    
    // Validate client_secret format (this is good practice)
    if (!clientSecret.startsWith('pi_') || !clientSecret.includes('_secret_')) {
      console.error('Invalid client_secret format:', clientSecret);
      return null;
    }
    
    return {
      clientSecret,
      appearance: {
        theme: 'night' as const,
        variables: {
          colorPrimary: '#10b981',
          colorBackground: '#000000',
          colorText: '#ffffff',
          colorDanger: '#ef4444',
          fontFamily: 'system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px',
        },
      },
    };
  }, [clientSecret]);

  // Get currency from cart items - no fallback, show raw amount if not available
  const currency = cartData?.cart?.items?.[0]?.currency;
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  // Calculate total price from cart data
  const totalPrice = cartData?.cart?.items?.reduce(
    (sum, item) => sum + item.subtotal + item.platform_fee,
    0
  ) || 0;

  // Handle payment success
  const handlePaymentSuccess = (paymentIntent: PaymentIntent) => {
    setPaymentIntentData(paymentIntent);
    setShowSuccessModal(true);
    // Cart count will auto-refresh when user navigates away (stale-while-revalidate)
  };

  // Handle payment error
  const handlePaymentError = (error: StripeError) => {
    setPaymentError(error);
    setShowFailureModal(true);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-black pt-24 pb-8">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Page Header Skeleton */}
            <div className="mb-8">
              <div className="h-6 w-32 bg-gray-800 rounded mb-4 animate-pulse"></div>
              <div className="h-8 w-48 bg-gray-800 rounded mb-2 animate-pulse"></div>
              <div className="h-4 w-64 bg-gray-800 rounded animate-pulse"></div>
            </div>

            {/* Order Summary Skeleton */}
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 mb-6 animate-pulse">
              <div className="h-6 w-32 bg-gray-800 rounded mb-4"></div>
              <div className="space-y-3 mb-4">
                <div className="h-4 w-full bg-gray-800 rounded"></div>
                <div className="h-4 w-3/4 bg-gray-800 rounded"></div>
              </div>
              <div className="h-6 w-24 bg-gray-800 rounded"></div>
            </div>

            {/* Payment Form Skeleton */}
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 animate-pulse">
              <div className="h-6 w-32 bg-gray-800 rounded mb-6"></div>
              <div className="h-64 bg-gray-800 rounded mb-4"></div>
              <div className="h-12 bg-gray-800 rounded"></div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !clientSecret || !stripeAccountId) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-black pt-24 pb-8 flex items-center justify-center">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-900 rounded-xl border border-red-500/30 p-8 text-center">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Payment Error</h2>
                <p className="text-gray-400 mb-6">{error || 'Failed to initialize payment'}</p>
                {!clientSecret && <p className="text-gray-500 text-sm">Reason: Missing Client Secret</p>}
                {!stripeAccountId && <p className="text-gray-500 text-sm">Reason: Missing Stripe Account ID</p>}
              </div>
              <button
                onClick={() => navigate(`/carts/${cartId}`)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Cart
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Timer display conditions
  const showTimer = cartData?.cart && (
    cartData.cart.status === CartStatus.PendingPayment || 
    cartData.cart.status === CartStatus.FailedPayment
  ) && cartData.cart.payment_intent_expires_at;

  return (
    <>
      <Header />

      <div className="min-h-screen bg-black pt-24 pb-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/carts/${cartId}`)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Cart
            </button>
            <h1 className="text-3xl font-bold text-white mb-2">Complete Payment</h1>
            <p className="text-gray-400">Secure payment powered by Stripe</p>
          </div>

          {/* Status Banner */}
          {cartData?.cart && (
            <CartStatusBanner
              status={cartData.cart.status}
              onCancelCheckout={handleCancelCheckout}
              isCanceling={isCancelingCheckout}
              isExpired={isExpired}
            />
          )}

          {/* Order Summary */}
          {cartData && (
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">Order Summary</h2>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal</span>
                  <span>
                    {formatPrice(cartData.cart.items?.reduce((sum, item) => sum + item.subtotal, 0) || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Platform Fees</span>
                  <span>
                    {formatPrice(cartData.cart.items?.reduce((sum, item) => sum + item.platform_fee, 0) || 0)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-800">
                <span className="text-white font-bold text-xl">Total</span>
                <span className="text-white font-bold text-xl">{formatPrice(totalPrice)}</span>
              </div>
            </div>
          )}

          {/* Payment Form */}
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
            <h2 className="text-xl font-bold text-white mb-6">Payment Details</h2>
            {options && clientSecret && stripePromise ? (
              <Elements stripe={stripePromise} options={options} key={clientSecret}>
                <CheckoutForm 
                  cartId={cartId!} 
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  timeRemaining={showTimer ? timeRemaining : null}
                  formattedTime={showTimer ? formattedTime : ''}
                  isExpired={isExpired}
                  onCancel={handleCancelCheckout}
                  isCanceling={isCancelingCheckout}
                />
              </Elements>
            ) : (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  Unable to initialize payment form. Please try refreshing the page or contact support.
                </p>
                {clientSecret && (
                  <p className="text-gray-500 text-xs mt-2">
                    Client secret: {clientSecret.substring(0, 20)}...
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Your payment information is secure and encrypted
            </p>
          </div>
        </div>
      </div>
      <Footer />

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        cartId={cartId!}
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        paymentIntentId={paymentIntentData?.id}
        isGuest={isGuest}
        clubHandle={cartData?.cart?.business?.handle || null}
      />

      {/* Payment Failure Modal */}
      <PaymentFailureModal
        isOpen={showFailureModal}
        onClose={() => setShowFailureModal(false)}
        onRetry={() => setShowFailureModal(false)}
        error={paymentError}
      />
    </>
  );
};

export default PaymentPage;

